/* eslint-disable */
import { useEffect, useState, useRef } from 'react';
import { Stack, Popover, PopoverPosition, Button, Box } from '@mui/material';
import dayjs from 'dayjs';

// Redux
import type { RootState } from '@/store';
import { useSelector, useDispatch } from 'react-redux';
import {
  setSelectedUnit,
  setIsApartmentView,
  setProjectApartments,
  setHoveredUnitId,
} from '@/features/project/projectSlice';

// New UrbanMix Component
import {
  BuildingView,
  BuildingViewRef,
  ApartmentInfo,
  ColorGradientVisualizer,
  EmbeddedModelLoader,
} from '@urbanmix-tech/um-embedded';

import { useAlert } from '@/contexts/Alert/AlertContext';

import { Building, Unit, LeaseLifecycle } from '@urbanmix-tech/shared-js';
import BuildingLegend from './BuildingLegend';

import { BuildingUnitPopup } from './BuildingUnitPopup';
import { useBuildingModel } from '../../contexts/Building/BuildingContext';
import { useLoading, useUIContext } from '@/shared/context/UIContextProvider';
import { useEmbeddedAuth } from '@/contexts/EmbeddedAuth';

export const leaseLifecycleStateToColor = {
  [LeaseLifecycle.NOTICE]: 'yellow',
  [LeaseLifecycle.INSPECT]: 'blue',
  [LeaseLifecycle.TURNOVER]: 'orange',
  [LeaseLifecycle.LEASE]: 'green',
  [LeaseLifecycle.DONE]: 'gray',
};

export type ValueRange = {
  minValue: number;
  maxValue: number;
  minColor: string;
  maxColor: string;
};

type BuildingModelProps = {
  building: Building;
  units: Unit[];
  selectedUnitIds: string[];  
  projectId: string;
  onSelectUnit(unitId: string | null): void;
  onHoverUnit(unitId: string | null): void;
};

type EmbeddedJWTResponse = {
  data: {
    jwt: string;
  };
};

const { VITE_UM_EMBEDDED_CLIENT_END_POINT } = import.meta.env;

export default function GenericBuildingModel({
  building,
  units,
  projectId,  
  onSelectUnit,
}: BuildingModelProps) {
  // Mount Redux dispatcher
  const dispatch = useDispatch();
  

  // Mount Alert
  const { showAlert } = useAlert();

  // Refs
  const buildingView = useRef<BuildingViewRef | null>(null);
  const cursorPosition = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const [highlightUnit, setHighlightUnit] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Redux Selectors
  const selectedUnit = useSelector(
    (state: RootState) => state.project.selectedUnit
  );

  const outsideHoveredUnitId = useSelector(
    (state: RootState) => state.project.hoveredUnitId
  );
  const hoveredActionItem = useSelector(
    (state: RootState) => state.project.hoveredActionItem
  );

  const highlightedUnits = useSelector(
    (state: RootState) => state.project.highlightedUnits
  );
  const projectUnits = useSelector(
    (state: RootState) => state.project.projectUnits
  );
  const isApartmentView = useSelector(
    (state: RootState) => state.project.isApartmentView
  );
  const apartmentData = useSelector(
    (state: RootState) => state.project.apartmentData
  );
  
  const projectVisualizer = useSelector(
    (state: RootState) => state.project.projectVisualizer
  );

  // React State
  const [hoveredApartment, setHoveredApartment] = useState<any>(null);
  const [apartments, setApartments] = useState<string[]>([]);
  const [rotateAround, setRotateAround] = useState(false);
  const [hoveredMeshId, setHoveredMeshId] = useState<string | null>(null);
  const [anchor, setAnchor] = useState<{
    unit: Unit;
    position: PopoverPosition;
  } | null>(null);

  const [buildingLoaded, setBuildingLoaded] = useState(false);

  const { hoveredUnitId, unitsOverlayData } = useBuildingModel();

  const [embeddedLoader, setEmbeddedLoader] =
    useState<EmbeddedModelLoader | null>(null);

  const { createLoading } = useLoading();
  const { jwt, isLoading: jwtLoading } = useEmbeddedAuth();

  useEffect(() => {
    console.log('^^^^getting loader for ', projectId);

    if (jwtLoading || !jwt) {
      // Wait for JWT to be available
      return;
    }

    setLoading(true);
    const loader = new EmbeddedModelLoader(
      projectId,
      jwt,
      VITE_UM_EMBEDDED_CLIENT_END_POINT
    );
    setEmbeddedLoader(loader);
    setLoading(false);
  }, [projectId, jwt, jwtLoading]);

  useEffect(() => {
    return () => {
      console.log('^^^^clean up generic building for ',projectId);
      setEmbeddedLoader(null);
    }
  },[]);

  // Effects
  // useEffect(() => {
  //   if (!isApartmentView) {
  //     switchToUnitView(selectedUnit);
  //     dispatch(setSelectedUnit(null));
  //     onSelectUnit(null);
  //   }
  // }, [isApartmentView]);

  useEffect(() => {
    // console.log('setHoveredActionItem', setHoveredActionItem?.unitId);
    if (buildingView.current) {
      buildingView.current.hoverApartment(hoveredActionItem ? hoveredActionItem.unitId : null);
    }
  }, [hoveredActionItem]);

  useEffect(() => {
    if (buildingView.current) {
      buildingView.current.hoverApartment(outsideHoveredUnitId);
    }
  }, [outsideHoveredUnitId]);

  useEffect(() => {
      console.log('highlightedUnits', highlightedUnits);
      // Only highlight after building is fully loaded to avoid null reference errors
      if (buildingView.current && buildingLoaded && highlightedUnits && highlightedUnits.length > 0) {
        // if (highlightedUnits.length === 1) {
        //   console.log('highlightedUnits pointing camera', highlightedUnits);
        //   buildingView.current.cameraToUnit(highlightedUnits[0].apartmentId);
        //   setHighlightUnit(highlightedUnits[0].apartmentId);
        //   // buildingView.current.highlightUnits(highlightedUnits.map( (unit) => unit.apartmentId));
        // } else {
         buildingView.current.highlightUnits(highlightedUnits.map( (unit) => unit.apartmentId),undefined,false);
        // }


      } else if (buildingLoaded) {
        console.log('clear highlight');
        buildingView.current?.endHighlightMode();
      }

  }, [highlightedUnits, buildingLoaded]);

  useEffect(() => {
    // switchToUnitView(selectedUnit);

    if (buildingView.current && selectedUnit) {
      // console.log('selectedUnit changed, focusing on unit:', selectedUnit.apartmentId);
      buildingView.current.cameraToUnit(selectedUnit.apartmentId);
      buildingView.current.selectApartment(selectedUnit.apartmentId,true);
    } else if (buildingView.current && !selectedUnit) {
      buildingView.current.selectApartment("");
    }
  }, [selectedUnit]);

  useEffect(() => {
    return () => {
      dispatch(setSelectedUnit(null));
    };
  }, []);

  useEffect(() => {
    
    if (buildingView.current) {
      // console.log("update data", apartmentData, projectVisualizer);
      if (apartmentData && projectVisualizer) {
        buildingView.current.setData(apartmentData, projectVisualizer);
      } else {
        buildingView.current.setData([],undefined);
      }
    }
  }, [apartmentData, projectVisualizer,buildingLoaded]);

  useEffect(() => {
    if (!building.modelPath) {
      showAlert('Not exists building model', 'error');
    }
  }, [building.modelPath, showAlert]);

  useEffect(() => {
    const mousePositionTracker = (e: MouseEvent) => {
      cursorPosition.current = {
        x: e.pageX,
        y: e.pageY,
      };
    };

    document.addEventListener('mousemove', mousePositionTracker);

    return () => {
      document.removeEventListener('mousemove', mousePositionTracker);
    };
  }, []);

  useEffect(() => {
    // console.log('hoveredApartment', hoveredApartment);
    if (hoveredApartment) {
      dispatch(setHoveredUnitId(hoveredApartment.apartmentId));
    } else {
      dispatch(setHoveredUnitId(null));
    }
  },[hoveredApartment])

  useEffect(() => {
    if (hoveredUnitId) {
      const meshId = units.find((unit) => unit.id === hoveredUnitId)?.meshId;
      if (meshId && meshId.includes('apt_')) {
        if (!meshId.includes('window')) {
          setHoveredMeshId(meshId);
        }
      }
    }
  }, [hoveredUnitId, units]);

  const handleZoomIn = () => {
    if (buildingView.current && selectedUnit) {
      buildingView.current.cameraToUnit(selectedUnit.apartmentId); // Adjust this method as needed for zooming in
    }
  };

  const handleZoomOut = () => {
    if (buildingView.current && selectedUnit) {
      buildingView.current.cameraToUnit(selectedUnit.apartmentId); // Adjust this method as needed for zooming out
    }
  };

  const handleFloorView = () => {
    if (!buildingView.current) return;

    let unitId = selectedUnit?.apartmentId ?? highlightUnit

    if (unitId) {
      const info = buildingView.current.getApartmentInfoById(unitId)
      buildingView.current.swithToFloorView(info.level);
    }        
  };

  const handleUnitView = () => {    
    let unitId = selectedUnit?.apartmentId ?? highlightUnit
    if (buildingView.current && unitId) {
      buildingView.current.focusOnUnit(unitId);
    }
  };

  const handleClose = () => {
    setAnchor(null);
  };

  const switchToUnitView = (apartment: any): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        if (buildingView.current && apartment) {
          buildingView.current.focusOnUnit(apartment.apartmentId);
        } else {
        }
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };

  const ignoredKeys = new Set(['id', 'apartmentId', 'meta', 'popupId', 'color','value','label','line','level','type','isActive']);

  const baseTheme = {
    type: "LiveLease",
    backgroundColor: "#09090b",
    floorColor: "#09090b", // Match background - floor becomes invisible

    // Enable transparent background so CSS gradient shows through
    transparent: true,

    // Lighting settings
    directionalLights: [
      {
        color: "#ffffff",
        intensity: 2.5,
        castShadow: true,
      },
      {
        color: "#e8e4f0",
        intensity: 2,
        castShadow: false,
      },
    ],

    // Enable camera-attached lighting for consistent illumination as camera rotates
    attachLightsToCamera: true,

    // Auto-fit configuration - controls camera framing of model
    autoFit: {
      fillRatio: 0.85, // Fill 85% of viewport
      distanceMultiplier: 1,
      offsetMultiplier: 0.55, // 45° camera angle
      minDistance: 10,
      fallbackDistance: 50,
    },

    transparentMaterialColor: 0x808080,
    addSky: false,
    ambientIntensity: 0.5,
    envMapIntensity: 0.8,
    shadows: {
      enabled: true, autoUpdate: true
    },
    cameraControls: {
      maxDistance: 500,
      minDistance: 10,
      maxPolarAngle: Math.PI / 2,
      screenSpacePanning: true,
    },
  };


  return (
    <Stack
      justifyContent="flex-start"
      alignItems="center"
      className="h-full w-full"
    >
      <Button
        className="absolute right-0 top-0"
        onClick={() => setRotateAround(!rotateAround)}
      >
        Rotate
      </Button>
      <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-800">
        {/* {selectedUnit ? (
          <Button
            className="absolute right-10 top-10 z-20"
            variant="outlined"
            color="info"
            onClick={() => {
              if (buildingView.current) buildingView.current.focusOnUnit(selectedUnit.apartmentId);
              // switchToUnitView(selectedUnit);
              // dispatch(setSelectedUnit(null));
              // onSelectUnit(null);
            }}
          >
            Close
          </Button>
        ) : null} */}
        {hoveredApartment && (
          <div className="absolute border-primary border-2 border-solid left-10 top-10 z-20 flex flex-col items-start justify-center rounded-xl bg-clear p-10">
            <div className="text-md">
              Apartment {hoveredApartment.apartmentId}
              { Array.from(Object.keys(hoveredApartment)).map((key) => {
                if (ignoredKeys.has(key)) {
                  return null;
                } else if (typeof hoveredApartment[key] === 'object') {
                  return null;
                }
                return (
                  <p key={key}>
                    {key}: {hoveredApartment[key]}
                  </p>
                );
              })}
              {/* {hoveredApartment.type ? ` - Type: ${hoveredApartment.type}` : ''} */}
              {hoveredApartment.label ? 
              <p>{`${hoveredApartment.label} ${hoveredApartment.value}`} 
              </p>: ''}
            </div>
           
          </div>
        )}
        {loading && <div>Loading...</div>}
        {embeddedLoader && (
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <BuildingView            
            ref={buildingView}
            loader={embeddedLoader}
            visualizer={projectVisualizer}
            theme={baseTheme}
            onBuildingLoaded={(apartments) => {
              console.log('building loaded', apartments);
              setBuildingLoaded(true);

              setApartments(apartments.map((apt : any) => typeof apt === 'string' ? apt : apt.apartmentId));
              dispatch(setProjectApartments(apartments));
              // setTimeout(() => {
              //   const view = {camera: {x: -158.431176206074, y: 52.6812104095378, z: -75.23831286105678},
              // targer: {x: -10.065956, y: 46.355083, z: -0.4309750000000001}}
              // const view2 = {camera:{x: 91.19905642156893, y: 59.31948760487493, z: 99.88974606710072},
              //     target: {x: -10.065956115722656, y: 46.35508346557617, z: -0.43097496032714844}}
              //   buildingView.current?.setCamera(view,true)
              // },100);
              // setTimeout(() => {   
              //   buildingView.current.rotateCamera(90,10)    
              // }, 2000);
              // // setTimeout(() => {   
              //   buildingView.current.rotateCamera(90,10)    
              // }, 12000);
            }}
            onApartmentSelected={(aptData: any) => {
              console.log('selected aptData', aptData);
              // dispatch(setIsApartmentView(true));
              dispatch(setSelectedUnit(aptData));
            }}
            onApartmentHover={(aptData: any) => {
              if (aptData) {
                const unitData = projectUnits.find(
                  (apartment) => apartment.id === aptData.apartmentId
                );
                aptData.meta = unitData;
              }
              setHoveredApartment(aptData);
            }}
            />
            <BuildingLegend visualizer={projectVisualizer} />
          </Box>
        )}
         <div className="absolute left-0 bottom-10 transform z-30 flex flex-row gap-16 px-16">
          {/* <Button variant="contained" onClick={handleZoomIn}>
            Zoom In
          </Button>
          <Button variant="contained" onClick={handleZoomOut}>
            Zoom Out
          </Button> */}
          <Button variant="outlined" color="info" onClick={handleFloorView} disabled={!selectedUnit && !highlightUnit}>
            Floor View
          </Button>
          
            <Button variant="outlined" color="info" onClick={handleUnitView} disabled={!selectedUnit && !highlightUnit}>
              Unit View
            </Button>
          
        </div>
      </div>
      <Popover
        open={Boolean(anchor)}
        anchorReference="anchorPosition"
        onClose={handleClose}
        anchorPosition={anchor?.position}
        slotProps={{
          paper: {
            className: 'bg-black !border-none',
          },
        }}
      >
        {anchor && (
          <BuildingUnitPopup
            unitId={anchor.unit.id}
            numBedrooms={anchor.unit.numBedrooms || 0}
            numBathrooms={anchor.unit.numBathrooms || 0}
            rent={anchor.unit.actualRent || 0}
            statusList={
              Array.isArray(anchor.unit.currentState) &&
              anchor.unit.currentState.length > 0
                ? anchor.unit.currentState
                : [
                    (anchor.unit.currentState as LeaseLifecycle) ||
                      LeaseLifecycle.NOTICE,
                  ]
            }
          />
        )}
      </Popover>
    </Stack>
  );
}
